import {
  computeOpenDebtRowsForOwner,
  isUnassignedDebtOwnerKey,
  localTodayIso,
  sumOpenDebtRowTotals,
  type CashbookDebtEntryRecord,
  type CashbookSettings,
  type OpenDebtRow
} from '@finvox/shared/cashbook';
import { format, parseISO, type Locale } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';

type Props = {
  open: boolean;
  onClose: () => void;
  ownerKey: string;
  settings: CashbookSettings;
  debtEntries: CashbookDebtEntryRecord[];
  asOfDate?: string;
};

function ownerLabel(ownerKey: string, t: (key: string) => string): string {
  if (isUnassignedDebtOwnerKey(ownerKey)) {
    return t('cashbook.debts.unassignedOpeningDebt');
  }
  return ownerKey;
}

function OpenDebtsTable({
  rows,
  locale,
  t
}: {
  rows: OpenDebtRow[];
  locale: Locale;
  t: (key: string, opts?: Record<string, string>) => string;
}) {
  const totals = sumOpenDebtRowTotals(rows);
  return (
    <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-lg border border-[var(--fv-border)]">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 z-[1] text-[var(--fv-text-muted)]">
          <tr>
            <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.colDate')}</th>
            <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.category')}</th>
            <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.memo')}</th>
            <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">{t('cashbook.debts.colOriginal')}</th>
            <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">{t('cashbook.debts.colRemaining')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--fv-border)]">
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="px-3 py-2 whitespace-nowrap">
                {format(parseISO(row.date), 'PP', { locale })}
              </td>
              <td className="px-3 py-2">
                {row.source === 'opening_debt'
                  ? t('cashbook.debts.openingRow')
                  : t(`cashbook.debts.categories.${row.categoryKey}`, {
                      defaultValue: row.categoryKey
                    })}
              </td>
              <td className="px-3 py-2 text-[var(--fv-text-muted)]">{row.memo ?? '—'}</td>
              <td className="px-3 py-2 text-end tabular-nums">
                <MoneyDisplay value={row.originalAmount} />
              </td>
              <td className="px-3 py-2 text-end font-medium tabular-nums">
                <MoneyDisplay value={row.remainingAmount} />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="sticky bottom-0 z-[1]">
          <tr className="border-t border-[var(--fv-border)] font-medium shadow-[0_-6px_12px_-8px_rgb(15_23_42_/_0.18)]">
            <td className="bg-[var(--fv-bg)] px-3 py-2" colSpan={3}>
              {t('cashbook.debts.totals')}
            </td>
            <td className="bg-[var(--fv-bg)] px-3 py-2 text-end tabular-nums">
              <MoneyDisplay value={totals.originalAmount} />
            </td>
            <td className="bg-[var(--fv-bg)] px-3 py-2 text-end tabular-nums">
              <MoneyDisplay value={totals.remainingAmount} />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function CashbookOwnerOpenDebtsDialog({
  open,
  onClose,
  ownerKey,
  settings,
  debtEntries,
  asOfDate = localTodayIso()
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? ar : enUS;

  const rows = useMemo(
    () => computeOpenDebtRowsForOwner(settings, debtEntries, asOfDate, ownerKey),
    [settings, debtEntries, asOfDate, ownerKey]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cashbook-owner-open-debts-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <Card className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden p-6 shadow-lg">
        <h2 id="cashbook-owner-open-debts-title" className="shrink-0 text-lg font-semibold">
          {t('cashbook.debts.ownerDetailTitle', { owner: ownerLabel(ownerKey, t) })}
        </h2>

        {rows.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('cashbook.debts.detailEmpty')}</p>
        ) : (
          <OpenDebtsTable rows={rows} locale={locale} t={t} />
        )}

        <div className="mt-6 flex shrink-0 justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
